import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { tap } from 'rxjs';
import { Router } from '@angular/router';
import { NotificationService } from '../../services/notification.service';
import { StorageService } from '../../services/storage.service';
import { environment } from '@env/global';

// TEMP: backend error messages that should never surface as a toast, no
// matter which call site triggers them. Wallet is "Coming soon" (nav-disabled),
// so its fetch failures shouldn't nag users — comment the entry out to
// re-enable once the wallet feature is back.
const SUPPRESSED_ERROR_MESSAGES = ['Wallet could not be fetched successfully.'];

function isSuppressedErrorMessage(message: unknown): boolean {
  if (typeof message !== 'string') {
    return false;
  }
  const normalized = message.trim().toLowerCase();
  return SUPPRESSED_ERROR_MESSAGES.some((m) => m.toLowerCase() === normalized);
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const baseAPI = environment.baseAPI;
  const notificationService = inject(NotificationService);
  const storageService = inject(StorageService);
  const router = inject(Router);

  return next(req).pipe(
    tap({
      next: (res: any) => {
        if (res.url?.includes(baseAPI) && res.body && !res.body.success) {
          if (!isSuppressedErrorMessage(res.body.message)) {
            notificationService.createNotification('error', res.body.message);
          }
          throw new Error(res.body.message);
        }
      },
      error: (err: HttpErrorResponse) => {
        switch (err.status) {
          case 401:
            notificationService.createNotification('error', 'Authentication needed');
            storageService.clear();
            router.navigate(['/']);
            break;
          case 400:
            if (!isSuppressedErrorMessage(err.error?.['message'])) {
              notificationService.createNotification(
                'error',
                err.error['message'] || err.error['title'],
              );
            }
            break;
          case 404:
            if (!isSuppressedErrorMessage(err.error?.['message'])) {
              notificationService.createNotification('error', err.error['message'] ?? 'Not Found');
            }
            break;
          default:
            if (!isSuppressedErrorMessage(err.error?.['message'])) {
              notificationService.createNotification(
                'error',
                err.error['message'] ?? 'An error occurred',
              );
            }
        }
      },
    }),
  );
};
